import { formatPhoneDisplay } from '../../utils/whatsapp';
import {
  RESERVED_SOURCES,
  SAMPLED_SOURCES,
  needsHeaderImage,
  needsPerMessageLink,
  previewParams,
  slotsFor,
} from './campaignWizardState';
import TemplatePreview from './TemplatePreview';

const PREVIEW_PEOPLE = 20;
const fieldClass = 'w-full px-3 py-2 text-base bg-card border border-input rounded-lg';

/** One mapping row for a reserved key (header_image/button_url), styled like a numbered slot row. */
const ReservedMappingRow = ({ label, slot, value, choices, onChange }) => (
  <div className="flex items-center gap-3">
    <span className="w-28 text-sm text-muted-foreground shrink-0">{label}</span>
    <select
      aria-label={`${label} source`}
      value={value ?? ''}
      onChange={(e) => onChange(slot, e.target.value)}
      className={fieldClass}
    >
      <option value="">Choose...</option>
      {choices.map((c) => (
        <option key={c.value} value={c.value}>
          {c.label}
        </option>
      ))}
    </select>
  </div>
);

const TemplatePicker = ({ templates, value, onChange }) => {
  if (templates.loading || (!templates.data && !templates.error)) {
    return <p className="text-sm text-muted-foreground">Loading templates...</p>;
  }
  if (templates.error) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-red-700 dark:text-red-300">{templates.error}</p>
        <button
          type="button"
          onClick={templates.reload}
          className="px-3 py-1 text-sm border border-input rounded-lg hover:bg-muted/50"
        >
          Try again
        </button>
      </div>
    );
  }
  if (templates.data.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">No approved templates yet. Sync them on the Templates tab first.</p>
    );
  }
  return (
    <select aria-label="Template" value={value} onChange={(e) => onChange(e.target.value)} className={fieldClass}>
      <option value="">Choose a template...</option>
      {templates.data.map((t) => (
        <option key={t.id} value={t.id}>
          {t.name}
        </option>
      ))}
    </select>
  );
};

/** Step 3: pick an approved template, map each {{n}} to a value, and see the message as a customer would. */
const MessageStep = ({ wizard }) => {
  const { state, patch, dispatch, lookups, template, preview, choices } = wizard;
  const people = (preview.data?.recipients ?? []).slice(0, PREVIEW_PEOPLE);
  const person = people[Math.min(state.previewAs, Math.max(0, people.length - 1))];
  const mapped = Object.values(state.variableMap);
  const usesSample = mapped.some((v) => SAMPLED_SOURCES.includes(v));
  const usesCompany = mapped.includes('custom:company_name');
  const hasMapping =
    template && (slotsFor(template).length > 0 || needsHeaderImage(template) || needsPerMessageLink(template));
  const choicesFor = (slot) => choices.filter((c) => RESERVED_SOURCES[slot].includes(c.value));

  return (
    <div className="grid gap-6 md:grid-cols-2">
      <div className="space-y-4 min-w-0">
        <div>
          <p className="mb-1 text-sm text-foreground/80">Template</p>
          <TemplatePicker
            templates={lookups.templates}
            value={state.templateId}
            onChange={(id) => dispatch({ type: 'template', id })}
          />
        </div>

        {template && hasMapping === false && (
          <p className="text-sm text-muted-foreground">This template has no placeholders to fill.</p>
        )}
        {template && hasMapping && (
          <div className="space-y-3">
            {slotsFor(template).length > 0 && (
              <p className="text-sm text-foreground/80">What should each placeholder show?</p>
            )}
            {slotsFor(template).map((slot) => (
              <div key={slot} className="flex items-center gap-3">
                <span className="w-12 font-mono text-sm text-muted-foreground shrink-0">{`{{${slot}}}`}</span>
                <select
                  aria-label={`Variable ${slot}`}
                  value={state.variableMap[slot] ?? ''}
                  onChange={(e) => dispatch({ type: 'map', slot, value: e.target.value })}
                  className={fieldClass}
                >
                  <option value="">Choose...</option>
                  {choices.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
            ))}
            {needsHeaderImage(template) && (
              <ReservedMappingRow
                label="Header image"
                slot="header_image"
                value={state.variableMap.header_image}
                choices={choicesFor('header_image')}
                onChange={(slot, value) => dispatch({ type: 'map', slot, value })}
              />
            )}
            {needsPerMessageLink(template) && (
              <ReservedMappingRow
                label="Button link"
                slot="button_url"
                value={state.variableMap.button_url}
                choices={choicesFor('button_url')}
                onChange={(slot, value) => dispatch({ type: 'map', slot, value })}
              />
            )}
          </div>
        )}

        {template && people.length > 0 && (
          <div>
            <p className="mb-1 text-sm text-foreground/80">Preview as</p>
            <select
              aria-label="Preview as"
              value={String(Math.min(state.previewAs, people.length - 1))}
              onChange={(e) => patch({ previewAs: Number(e.target.value) })}
              className={fieldClass}
            >
              {people.map((p, i) => (
                <option key={p.source_id ?? `${p.phone}-${i}`} value={String(i)}>
                  {p.name || formatPhoneDisplay(p.phone)}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="min-w-0">
        {template ? (
          <>
            <TemplatePreview template={template} params={previewParams(state, template, person)} />
            {usesSample && (
              <p className="mt-2 text-xs text-center text-muted-foreground">
                Preview uses sample values for placeholders not yet filled in for this contact
              </p>
            )}
            {usesCompany && (
              <p className="mt-2 text-xs text-center text-muted-foreground">
                Preview uses a sample value for company name
              </p>
            )}
          </>
        ) : (
          <p className="p-6 text-sm text-center text-muted-foreground border border-dashed border-input rounded-lg">
            Choose a template to see the message.
          </p>
        )}
      </div>
    </div>
  );
};

export default MessageStep;
